/**
 * TASK EDIT FORM BODY — Form di modifica inline dei campi di un Task.
 *
 * Estratto dal vecchio TaskEditDrawer per riusabilita': stesso body
 * funziona dentro un SidePanel (TaskEditDrawer per il flusso TaskList)
 * e dentro un AccordionDetails (ActivityTimeline, embed nella timeline
 * delle attivita').
 *
 * Pattern di edit:
 *  - La maggior parte dei campi e' un <EditableField> (doppio click per
 *    entrare in edit, blur/Enter per committare).
 *  - Due campi sono Autocomplete inline-edit (Assegnato a, Associa ai
 *    record): performance e lista completa sempre disponibile.
 *  - Lo status è readonly (derivato backend; la transizione di stato
 *    passa da un endpoint dedicato, non da questo form).
 *
 * Visibilita' per-tipo:
 *  Il body legge la config da getTaskFieldConfig(task.type). Per
 *  nascondere un campo basta dichiararlo nella mappa per quel tipo.
 *
 * Scope relationships: il binder in edit mode lavora su scope locale
 * (fetch/create/delete via API diretta, no global slice). È sicuro
 * montare il body sia in TaskEditDrawer standalone sia embed in
 * ActivityTimeline senza interferire con le relationships del parent
 * Detail (Person/Business/Quotation).
 */
import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useServerPagedSearch, useLazyResolution } from '@crm/shared';
import { EditableField, type EditableValue, type LookupOption } from '../../components/EditableField';
import { RichTextField } from '../../components/RichTextField';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchPipelines } from '../pipeline/pipelineThunks';
import { pipelinesOfType } from '../pipeline/pipelineEntityTypes';
import { pipelineStateLabel, sortedStates } from '../pipeline/pipelineUtils';
import type { PipelineSliceState } from '../pipeline/pipelineTypes';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { useToast } from '../../components/useToast';
import { queueApi } from '../queue/queueApi';
import { userApi } from '../user/userApi';
import { updateTask } from './taskThunks';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { useAssociatedRecordsBinder } from '../../components/useAssociatedRecordsBinder';
import { ActivityTools } from '../../components/ActivityTools';
import { TASK_TYPES, TASK_PRIORITIES, type Task, type TaskCreatePayload, type TaskUpdatePayload } from './taskTypes';
import { TASK_RECURRENT_UNITS, type TaskRecurrentUnit } from './taskRecurrentUnits';
import { getTaskFieldConfig, type TaskFieldConfigMap } from './taskFieldConfig';
import { TaskStatusIcon } from './TaskStatusIcon';
import type { Queue } from '../queue/queueTypes';
import type { User } from '../user/userTypes';

interface TaskEditFormBodyProps {
  task: Task;
  /**
   * Override puntuale della config per-tipo. Permette al call site di
   * nascondere campi senza modificare la mappa per-tipo (resta come
   * meccanismo per future regole per-tipo).
   */
  fieldConfigOverride?: TaskFieldConfigMap;
}

interface AssignableUser { id: number; fullName: string; }

async function fetchQueueOptions(groupId: number, query: string): Promise<LookupOption[]> {
  try {
    const page = await queueApi.getAll(groupId, {
      search: query || undefined,
      pageSize: 20,
    });
    return page.items.map((qu: Queue) => ({ id: qu.queueId, label: qu.name }));
  } catch {
    return [];
  }
}

export function TaskEditFormBody({ task, fieldConfigOverride }: TaskEditFormBodyProps) {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { t } = useTranslation();
  const groupId = useCurrentGroupId();

  const fieldConfig = useMemo(
    () => ({ ...getTaskFieldConfig(task.type), ...fieldConfigOverride }),
    [task.type, fieldConfigOverride],
  );

  // --- Risoluzione on-demand del nome della coda (queue) per il display.
  const queueName = useLazyResolution<Queue, string>(
    task.queueId,
    (id) => queueApi.getById(id),
    (q) => q.name,
  );

  const fetchQueueOptionsBound = useCallback(
    (q: string) => fetchQueueOptions(groupId, q),
    [groupId]
  );

  // --- Assegnato a: lookup inline-edit ---
  const [userSavingId, setUserSavingId] = useState<number | null>(null);
  const [editingUser, setEditingUser] = useState(false);
  const currentUser = useLazyResolution<User, AssignableUser>(
    task.assignedUserId,
    (id) => userApi.getById(id, groupId),
    (u) => ({ id: u.id, fullName: u.fullName }),
  );

  const {
    loading: usersLoading,
    autocompleteProps: userAutocompleteProps,
    listboxProps: userListboxProps,
  } = useServerPagedSearch<AssignableUser>({
    fetchPage: async (q, page, size) => {
      const list = await userApi.search(q, groupId);
      const start = (page - 1) * size;
      return {
        items: list.slice(start, start + size).map((u: User) => ({ id: u.id, fullName: u.fullName })),
        totalCount: list.length,
      };
    },
    pageSize: 20,
    debounceMs: 300,
    resetOn: [groupId],
  });

  async function handleUserChange(newUser: AssignableUser | null) {
    if (!newUser || newUser.id === task.assignedUserId) return;
    setUserSavingId(newUser.id);
    try {
      const payload: TaskUpdatePayload = { taskId: task.taskId, assignedUserId: newUser.id };
      await dispatch(updateTask(payload)).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setUserSavingId(null);
    }
  }

  // --- Associa ai record: picker inline-edit (binder edit mode) ---
  const taskFqn = getEntityFqn('Task');
  const {
    records: currentlyLinked,
    onRecordsChange: handleRecordsChange,
    recordsSaving,
    pickerProps: recordsPickerProps,
  } = useAssociatedRecordsBinder({
    mode: 'edit',
    entityFqn: taskFqn ?? '',
    entityId: task.taskId,
  });

  const commitField =
    (field: keyof TaskCreatePayload) =>
    async (newValue: EditableValue) => {
      try {
        const payload = { taskId: task.taskId, [field]: newValue } as TaskUpdatePayload;
        await dispatch(updateTask(payload)).unwrap();
        toast({ severity: 'success', message: t('common.saved') });
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    };

  // Tutti gli stati delle pipeline DI TIPO TASK, con davanti il nome della pipeline. La voce
  // vuota vale "nessuna collocazione", che per un task e' normale.
  const allPipelines = useAppSelector((s2) => (s2.pipelines as PipelineSliceState).items);
  // Senza questo carico il campo non comparirebbe affatto quando nessun'altra schermata ha gia'
  // letto le pipeline: niente errore, solo una riga assente. Un difetto che si nota per caso.
  useEffect(() => {
    if (allPipelines.length === 0) dispatch(fetchPipelines({ pageSize: 1000 }));
  }, [allPipelines.length, dispatch]);

  const pipelineStateOptions = useMemo(() => {
    const opts = [{ value: '', label: t('common.none') }];

    for (const p of pipelinesOfType(allPipelines, getEntityFqn('Task'))) {
      for (const st of sortedStates(p)) {
        opts.push({
          value: String(st.pipelineStateId),
          label: `${p.name} — ${pipelineStateLabel(st)}`,
        });
      }
    }

    return opts.length > 1 ? opts : [];
  }, [allPipelines, t]);

  async function saveTaskPatch(patch: Partial<TaskUpdatePayload>) {
    try {
      await dispatch(updateTask({ taskId: task.taskId, ...patch })).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  // Ricorrenza: unit='none' significa non ricorrente. I tre campi (recurrent,
  // recurrentValue, recurrentUnit) viaggiano insieme per restare coerenti:
  // scegliere un'unità reale attiva recurrent e garantisce value >= 1; 'none'
  // azzera tutto.
  function handleRecurrentUnitChange(unit: TaskRecurrentUnit | 'none') {
    if (unit === 'none') {
      saveTaskPatch({ recurrent: false, recurrentUnit: 'none', recurrentValue: 0 });
    } else {
      saveTaskPatch({
        recurrent: true,
        recurrentUnit: unit,
        recurrentValue: task.recurrentValue > 0 ? task.recurrentValue : 1,
      });
    }
  }

  // Layout allineato all'immagine PDF "Maschere attivita' / Task":
  //  1. Titolo (con indicatore di stato accanto, readonly)
  //  2. Grid 2-col: Data di scadenza | Promemoria
  //  3. Checkbox "Imposta ripetizione" (con value+unit espansi se attiva)
  //  4. Grid 4-col: Tipo di task | Priorita' | Coda | Assegnato a
  //  5. Note task (full-width)
  //  6. Associa ai record (full-width)
  // L'header dell'Accordion (parent ActivityTimeline) fornisce gia' "Task
  // assegnato a X / Scadenza Y" come label readonly, quindi qui non si
  // ripetono.
  const showRow2 = !fieldConfig.expirationDate?.hidden || !fieldConfig.remindDate?.hidden;
  const showRow4 =
    !fieldConfig.type?.hidden ||
    !fieldConfig.priority?.hidden ||
    !fieldConfig.queueId?.hidden ||
    !fieldConfig.assignedUserId?.hidden;
  return (
    <div className="task-edit-body">
      {!fieldConfig.title?.hidden && (
        <div className="task-edit-body__title-row">
          {/* Indicatore di stato readonly accanto al titolo (PDF). La
              transizione di stato e' su endpoint dedicato (taskApi.updateStatus). */}
          {!fieldConfig.status?.hidden && (
            <TaskStatusIcon status={task.status} className="task-edit-body__status-icon" />
          )}
          <div className="task-edit-body__title-input">
            <EditableField
              value={task.title}
              variant={{ kind: 'text' }}
              required
              onCommit={commitField('title')}
              label={t('task.taskTitle')}
            />
          </div>
        </div>
      )}

      {showRow2 && (
        <div className="task-edit-body__grid task-edit-body__grid--2col">
          {!fieldConfig.expirationDate?.hidden && (
            <FieldRow label={t('task.expirationDate')}>
              <EditableField
                value={task.expirationDate}
                variant={{ kind: 'datetime' }}
                onCommit={commitField('expirationDate')}
                label={t('task.expirationDate')}
              />
            </FieldRow>
          )}
          {!fieldConfig.remindDate?.hidden && (
            <FieldRow label={t('task.remindDate')}>
              <EditableField
                value={task.remindDate}
                variant={{ kind: 'datetime' }}
                onCommit={commitField('remindDate')}
                label={t('task.remindDate')}
              />
            </FieldRow>
          )}
        </div>
      )}

      {!fieldConfig.recurrence?.hidden && (
        <FieldRow label={t('task.recurrence')}>
          <div className="task-edit-body__recurrence">
            {/* Etichetta a sinistra (come gli altri campi) + box on/off: qui la
                label la fornisce il FieldRow, quindi il checkbox resta "nudo". */}
            <Checkbox
              size="small"
              checked={task.recurrent}
              onChange={(e) => {
                if (e.target.checked) {
                  // Riprende l'unita' precedente se valida, altrimenti default 'day'.
                  const unit: TaskRecurrentUnit =
                    task.recurrentUnit && task.recurrentUnit !== 'none' ? task.recurrentUnit : 'day';
                  handleRecurrentUnitChange(unit);
                } else {
                  handleRecurrentUnitChange('none');
                }
              }}
            />
            {task.recurrent && (
              <div className="task-edit-body__recurrence-expand">
              <EditableField
                value={String(task.recurrentValue)}
                variant={{ kind: 'text' }}
                onCommit={(v) => commitField('recurrentValue')(Number(v) || 1)}
                label={t('task.recurrentValue')}
              />
              <TextField
                select
                size="small"
                value={task.recurrentUnit}
                onChange={(e) => handleRecurrentUnitChange(e.target.value as TaskRecurrentUnit)}
                aria-label={t('task.recurrence')}
              >
                {TASK_RECURRENT_UNITS.map((u) => (
                  <MenuItem key={u.value} value={u.value}>{t(u.label)}</MenuItem>
                ))}
              </TextField>
              </div>
            )}
          </div>
        </FieldRow>
      )}

      {showRow4 && (
        <div className="task-edit-body__grid task-edit-body__grid--4col">
          {!fieldConfig.type?.hidden && (
            <FieldRow label={t('task.taskType')}>
              <EditableField
                value={task.type}
                variant={{ kind: 'select', options: TASK_TYPES }}
                required
                onCommit={commitField('type')}
                label={t('task.taskType')}
              />
            </FieldRow>
          )}
          {!fieldConfig.priority?.hidden && (
            <FieldRow label={t('task.priority')}>
              <EditableField
                value={task.priority}
                variant={{ kind: 'select', options: TASK_PRIORITIES }}
                required
                onCommit={commitField('priority')}
                label={t('task.priority')}
              />
            </FieldRow>
          )}
          {!fieldConfig.queueId?.hidden && (
            <FieldRow label={t('task.queue')}>
              <EditableField
                value={task.queueId}
                variant={{
                  kind: 'lookup',
                  fetcher: fetchQueueOptionsBound,
                  displayLabel: queueName ?? (task.queueId ? `#${task.queueId}` : ''),
                }}
                onCommit={commitField('queueId')}
                label={t('task.queue')}
              />
            </FieldRow>
          )}
          {/* Stato di pipeline: qui e' UNA tendina sola e non la cascata del form di
              creazione, perche' questo form modifica un valore per riga. Le voci portano il nome
              della pipeline davanti allo stato, cosi' due pipeline con uno stato omonimo restano
              distinguibili — sceglierne una a caso metterebbe il task nella pipeline sbagliata, e
              nessuno se ne accorgerebbe guardando la bacheca. */}
          {pipelineStateOptions.length > 0 && (
            <FieldRow label={t('task.pipelineState')}>
              <EditableField
                value={String(task.currentStateId || '')}
                variant={{ kind: 'select', options: pipelineStateOptions }}
                onCommit={(v) => saveTaskPatch({ currentStateId: Number(v) || 0 })}
                label={t('task.pipelineState')}
              />
            </FieldRow>
          )}
          {!fieldConfig.assignedUserId?.hidden && (
            <FieldRow label={t('task.assignedTo')}>
              {editingUser ? (
                <Autocomplete
                  {...userAutocompleteProps}
                  value={currentUser}
                  onChange={(_, val) => { handleUserChange(val); setEditingUser(false); }}
                  onClose={(_, reason) => { if (reason !== 'selectOption') setEditingUser(false); }}
                  getOptionLabel={(o) => o.fullName}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  disabled={userSavingId != null}
                  loading={usersLoading}
                  openOnFocus
                  slotProps={{ listbox: userListboxProps }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" autoFocus />
                  )}
                />
              ) : (
                <div
                  className="editable-field editable-field--view"
                  role="button"
                  tabIndex={0}
                  aria-label={t('task.assignedTo')}
                  onDoubleClick={() => setEditingUser(true)}
                  onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingUser(true); }
                  }}
                >
                  <span className="editable-field__value">
                    {currentUser?.fullName ?? '—'}
                  </span>
                </div>
              )}
            </FieldRow>
          )}
        </div>
      )}

      {!fieldConfig.notes?.hidden && (
        <FieldRow label={t('common.notes')}>
          <RichTextField
            value={task.notes}
            onCommit={commitField('notes')}
            placeholder={t('task.notesPlaceholder')}
          />
        </FieldRow>
      )}

      <ActivityTools
        {...recordsPickerProps}
        records={currentlyLinked}
        onChange={(val) => handleRecordsChange?.(val)}
        recordsSaving={recordsSaving}
        entityFqn={taskFqn ?? ''}
        entityId={task.taskId}
        hideAssociations={fieldConfig.associatedRecords?.hidden}
      />
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="field-row">
      <div className="field-row__label text-xs text-muted">{label}</div>
      <div>{children}</div>
    </div>
  );
}
